import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TransportModesPanelMode } from 'components/RoutePlanner/TransportModesPanel'
import {
  RoutePlannerState,
  RoutePlannerStatus,
  RouteWaypoint,
  StateRoute,
  StateRouteTransportGroup,
} from 'data/model/routePlanner'
import { produce } from 'immer'
import { ResultItem } from 'map/services'
import { SerializedRecentItem } from 'utils/storage'

const initialState: RoutePlannerState = {
  routeWaypoints: [],
  routes: [],
  routesAutoSuggestLocations: [],
  routesRecentLocations: [],
  selectedTransportMode: TransportModesPanelMode.Car,
}

const routePlannerSlice = createSlice({
  initialState,
  name: 'routePlanner',
  reducers: {
    fetchRoutes: {
      prepare: (waypoints: RouteWaypoint[], transportMode: TransportModesPanelMode) => {
        return { payload: { transportMode, waypoints } }
      },
      reducer: (state: RoutePlannerState) => {
        return produce(state, (draft) => {
          draft.status = RoutePlannerStatus.ROUTES_PENDING
        })
      },
    },
    setError(state: RoutePlannerState, action: PayloadAction<string>) {
      return produce(state, (draft) => {
        draft.error = action.payload
      })
    },
    setRouteWaypointList(state: RoutePlannerState, action: PayloadAction<RouteWaypoint[]>) {
      return produce(state, (draft) => {
        draft.routeWaypoints = action.payload
      })
    },
    setRouteWaypoints: {
      prepare: (waypoints: RouteWaypoint[]) => {
        return { payload: { waypoints } }
      },
      reducer: (state: RoutePlannerState) => {
        return state
      },
    },
    setRoutes(state: RoutePlannerState, action: PayloadAction<StateRoute[]>) {
      return produce(state, (draft) => {
        draft.routes = action.payload
      })
    },
    setRoutesAutoSuggestLocations(state: RoutePlannerState, action: PayloadAction<ResultItem[]>) {
      return produce(state, (draft) => {
        draft.routesAutoSuggestLocations = action.payload
      })
    },
    setRoutesRecentLocations(
      state: RoutePlannerState,
      action: PayloadAction<SerializedRecentItem[]>
    ) {
      return produce(state, (draft) => {
        draft.routesRecentLocations = action.payload
      })
    },
    setSelectedRoute(state: RoutePlannerState, action: PayloadAction<StateRoute | undefined>) {
      return produce(state, (draft) => {
        draft.selectedRoute = action.payload
      })
    },
    setSelectedTransportMode(
      state: RoutePlannerState,
      action: PayloadAction<TransportModesPanelMode>
    ) {
      return produce(state, (draft) => {
        draft.selectedTransportMode = action.payload
      })
    },
    setSelectedTransportOption(
      state: RoutePlannerState,
      action: PayloadAction<StateRouteTransportGroup | undefined>
    ) {
      return produce(state, (draft) => {
        draft.selectedTransportOption = action.payload
      })
    },
    setStatus(state: RoutePlannerState, action: PayloadAction<RoutePlannerStatus>) {
      return produce(state, (draft) => {
        draft.status = action.payload
      })
    },
  },
})

export const {
  fetchRoutes,
  setError,
  setRouteWaypoints,
  setRoutes,
  setRoutesAutoSuggestLocations,
  setRoutesRecentLocations,
  setSelectedRoute,
  setSelectedTransportOption,
  setSelectedTransportMode,
  setStatus,
  setRouteWaypointList,
} = routePlannerSlice.actions
export const reducer = routePlannerSlice.reducer
